import Link from "next/link";
import { getGlobalData } from "utils/api";
import { getLocalizedPaths } from "utils/localize";
import Image from "next/image";

import Breadcrumb from "@/components/brandRefresh/common/Breadcrumb";
import { RefreshHeader } from "@/components/brandRefresh/common/PageTemplate";

export default function FinancialSupport({ metadata }) {
  return (
    <RefreshHeader metadata={metadata}>
      <div className="flex flex-col gap-spacing-12 lg:gap-spacing-14 text-sm lg:text-base">
        <Breadcrumb />

        <div className="flex gap-4 items-start flex-col md:flex-row lg:flex-row 4xl:text-xl">
          <Image
            src="/images/icons/heart.svg"
            alt="book-icon"
            className="p-1 h-8 w-8"
            width="32"
            height="32"
          />
          <div>
            <h3 className="text-onmoText text-2xl font-medium mb-2">
              Financial support to suit your needs
            </h3>
            <p>
              We understand that life can be unpredictable. Unexpected bills can
              pop up, or monthly expenses can be higher at different times of
              the year. If you find yourself in this type of situation and are
              worried about your upcoming Onmo payment there’s help that is
              available to you.
            </p>
            <p className="mt-6">
              For a full list of our FAQs related to financial support, please
              <Link
                className="text-onmoPrimary"
                target="_blank"
                rel="noopener noreferrer"
                href={"/help/faq/financial-support"}
              >
                {" "}
                click here.
              </Link>
            </p>
          </div>
        </div>

        <div className="flex gap-4 items-start flex-col md:flex-row lg:flex-row 4xl:text-xl">
          <Image
            src="/images/icons/notification.svg"
            alt="headphone-icon"
            className="p-1 h-8 w-8"
            height="32"
            width="32"
          />
          <div>
            <h3 className="text-onmoText text-2xl font-medium  mb-2">
              Early signs of financial difficulty and what you should do
            </h3>
            <p>
              Some of the early signs of financial difficulty can include only
              being able to pay the minimum payments, missing payments, needing
              to take more credit to keep up with your repayments, or having
              unexpected expenses stretching your budget.
            </p>
            <p className="mt-6">
              If you’re going through this, the best thing to do is contact our
              Specialist Customer Care Team. We’re here to help and understand
              your situation to discuss options available to you. You can reach
              our team by phone{" "}
              <Link
                className="text-onmoPrimary"
                target="_blank"
                rel="noopener noreferrer"
                href="/contact-us"
              >
                {" "}
                here{" "}
              </Link>{" "}
              during our opening hours, just press 2 to get connected. You can
              also reach us by email at help@onmo.app.
            </p>
            <p className="mt-6">
              The earlier you reach out, the more we can do to help.
            </p>
          </div>
        </div>

        <div className="flex gap-3 items-start flex-col md:flex-row lg:flex-row 4xl:text-xl">
          <Image
            src="/images/icons/headphone.svg"
            alt="eye-icon"
            className="m-1 ml-0 h-6 w-6"
            height="24"
            width="24"
          />
          <div>
            <h3 className="text-onmoText text-2xl font-medium mb-2">
              Letting us know if you’re unable to make a payment
            </h3>
            <p>
              If you know that you won’t be able to make any payments, it’s
              important to let us know as soon as you can. We may be able to
              offer options such as temporary reduced payments or agree a
              promise-to-pay, which can give you some time to get back on track.
            </p>
            <p className="mt-6">
              We also have a budgeting tool that can help us work with you to
              figure out what level of repayment would be affordable, based on
              your current financial situation. No matter what option suits you
              best
            </p>
          </div>
        </div>
        <div className="flex gap-3 items-start flex-col md:flex-row lg:flex-row 4xl:text-xl">
          <Image
            src="/images/icons/more-info.svg"
            alt="eye-icon"
            className="m-1 ml-0 h-6 w-6"
            height="24"
            width="24"
          />
          <div>
            <h3 className="text-onmoText text-2xl font-medium mb-2">
              Free independent debt advice is available if you need it before
              contacting us
            </h3>
            <p>
              If you’d like independent advice, there are several UK-based
              organisations that provide free help and support:
            </p>
            <p className="mt-6">
              StepChange:{" "}
              <Link
                className="text-onmoPrimary"
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.stepchange.org"
              >
                {" "}
                www.stepchange.org{" "}
              </Link>{" "}
              or call 0800 138 1111
            </p>
            <p className="mt-6">
              PayPlan:{" "}
              <Link
                className="text-onmoPrimary"
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.payplan.com"
              >
                www.payplan.com
              </Link>{" "}
              or call 0800 280 2816
            </p>
            <p className="mt-6">
              Citizens Advice:
              <Link
                className="text-onmoPrimary"
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.citizensadvice.org.uk"
              >
                {" "}
                www.citizensadvice.org.uk
              </Link>{" "}
              or call 0800 144 8848
            </p>
            <p className="mt-6">
              These organisations can offer guidance on managing your finances
              and dealing with debt.
            </p>
            <p className="mt-6">
              If you are looking for additional support, you can also reach out
              to Samaritans at 116 123 (Free), MIND at 0300 123 3393, or NHS
              Emergency Services at 111 if you need someone to talk to during a
              challenging time.
            </p>
          </div>
        </div>
        <div className="flex gap-3 items-start flex-col md:flex-row lg:flex-row 4xl:text-xl">
          <Image
            src="/images/icons/profile.svg"
            alt="eye-icon"
            className="m-1 ml-0 !h-8 !w-8"
            height="32"
            width="32"
          />
          <div>
            <h3 className="text-onmoText text-2xl font-medium mb-2">
              No matter your circumstances, we’re here to help
            </h3>
            <p>
              If you don’t do anything about your financial difficulties, it
              could lead to missed payments, defaults, and ultimately legal
              action. This could have serious consequences for your credit file
              and ability to get credit in the future.
            </p>
            <p className="mt-6">
              Please don’t hesitate to{" "}
              <Link
                className="text-onmoPrimary"
                target="_blank"
                rel="noopener noreferrer"
                href="/contact-us"
              >
                contact us{" "}
              </Link>{" "}
              – our Specialist Customer Care Team is here to help.
            </p>
          </div>
        </div>
        <div className="flex gap-3 items-start flex-col md:flex-row lg:flex-row 4xl:text-xl">
          <Image
            src="/images/icons/message.svg"
            alt="eye-icon"
            className="m-1 ml-0 h-8 w-6"
            height="32"
            width="24"
          />
          <div>
            <h3 className="text-onmoText text-2xl font-medium mb-2">
              Registering a Complaint
            </h3>
            <p>
              We’re committed to providing the best possible service to our
              customers, but we know things don’t always go right. If you need
              to register a complaint with us, please visit our{" "}
              <Link
                className="text-onmoPrimary"
                target="_blank"
                rel="noopener noreferrer"
                href="/help/customer-care/complaints"
              >
                {" "}
                Complaints page{" "}
              </Link>
              for more information.
            </p>
          </div>
        </div>
      </div>
    </RefreshHeader>
  );
}

export async function getServerSideProps(context) {
  const { locale, locales, defaultLocale } = context;
  const globalLocale = await getGlobalData(locale);

  const pageContext = {
    locale,
    locales,
    defaultLocale,
    slug: context.resolvedUrl.replace(/\//g, ""),
  };

  const localizedPaths = getLocalizedPaths(pageContext);

  const metadata = {
    metaTitle: `Financial Support`,
  };

  return {
    props: {
      metadata,
      global: globalLocale.data,
      pageContext: {
        ...pageContext,
        localizedPaths,
      },
    },
  };
}
